import { useQuery } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { useProductCenterApi } from 'api';
import { getServiceProtocolQuery } from 'api/queries';
import { getClientTimeInISO } from 'library/utils/dateTime/clientTime';
import { vppnReverseProxyType } from 'main/auth/provider';
import {
	PdfPrintFormLayout,
	PdfPrintSwitchConfig,
	PdfPrintTab,
} from 'modules/Shared/PdfPrint/PdfPrintFormLayout';
import { PrintPdfDialogContentProps } from 'modules/Shared/PdfPrint/types';
import { usePdfPreview } from 'modules/Shared/PdfPrint/usePdfPreview';

import useStorePdfServiceProtocolPrintoutSettings from '../Hooks/useStorePdfPrintoutSettings';
import { usePdfFormSubmit } from './PdfFormSubmit';
import { PrintableAttachments } from './PrintableAttachments';
import {
	PrintProtocolFormType,
	PrintProtocolUserSettings,
	mapFormToPdfRequest,
} from './Types';

interface PrintProtocolDialogContentProps
	extends PrintPdfDialogContentProps<PrintProtocolUserSettings> {
	isOffline: boolean;
}

export const PrintProtocolDialogContent = ({
	id,
	isOffline,
	onClose,
	previewDebounce = 1500,
	defaultPdfSettings,
	isDealer,
	setIsDealer,
	isPreview,
	setIsPreview,
}: PrintProtocolDialogContentProps) => {
	const { t } = useTranslation(['commonButton', 'serviceProtocol', 'common']);

	const { api } = useProductCenterApi();

	const updatePdfSettings = useStorePdfServiceProtocolPrintoutSettings();
	const { data: protocolData } = useQuery({
		...getServiceProtocolQuery(api, id, false),
	});

	const onTabChange = (_: React.SyntheticEvent, newValue: PdfPrintTab) => {
		void updatePdfSettings(getValues(), newValue !== 'dealer'); // save settings for previous tab
		setIsDealer(newValue === 'dealer');
	};

	const onButtonClick = () => {
		setIsPreview(true);
	};

	const mapFormToRequest = useCallback(
		(pId: number, form: PrintProtocolFormType) =>
			mapFormToPdfRequest(
				pId,
				form,
				vppnReverseProxyType(),
				isOffline,
				isDealer
			),
		[isDealer, isOffline]
	);

	const { onSubmit, control, isPending, getValues } = usePdfFormSubmit({
		isDealer,
		protocolId: id,
		chassisSeries: protocolData?.chassisSeries ?? '',
		chassisNumber: protocolData?.chassisNumber ?? 0,
		mapFormToRequest,
		defaultValues: isDealer
			? defaultPdfSettings?.forDealer
			: defaultPdfSettings?.forCustomer,
	});

	const onSubmitWithSaveSettings = (
		event: React.SubmitEvent<HTMLFormElement>
	) => {
		void updatePdfSettings(getValues(), isDealer);
		void onSubmit(event);
	};

	const getFileName = useCallback(
		() =>
			`Service Protocol ${protocolData?.chassisSeries}${protocolData?.chassisNumber} ${getClientTimeInISO()}.pdf`,
		[protocolData]
	);

	const { previewUrl, isLoadingPreview, triggerPreview } = usePdfPreview({
		generate: (signal) =>
			api
				.downloadServiceProtocolPdf(
					mapFormToRequest(id, getValues()),
					{ openFile: false },
					{ format: 'blob', signal }
				)
				.then((response) => response.data),
		getFileName,
		previewDebounce,
		onError: () => setIsPreview(false),
		disabled: !isPreview,
	});

	const onChangeHandler = useCallback(() => {
		if (isPreview) {
			triggerPreview();
		}
	}, [isPreview, triggerPreview]);

	useEffect(() => {
		// covers turning preview on and switching tabs while preview is active
		if (isPreview) {
			triggerPreview();
		}
	}, [isPreview, isDealer, triggerPreview]);

	const switches = useMemo<PdfPrintSwitchConfig<PrintProtocolFormType>[]>(
		() => [
			...(isDealer
				? [
						{
							name: 'printQr' as const,
							label: t('serviceProtocol:protocolPrintout-qr'),
							helperText: t(
								'serviceProtocol:protocolPrintout-qr-helper'
							),
						},
					]
				: []),
			...(!isOffline
				? [
						{
							name: 'printEstimatedHours' as const,
							label: t(
								'serviceProtocol:protocolPrintout-estimatedHours'
							),
						},
						{
							name: 'printUsedHours' as const,
							label: t(
								'serviceProtocol:protocolPrintout-usedHours'
							),
						},
					]
				: []),
			{
				name: 'printConfiguration',
				label: t('serviceProtocol:protocolPrintout-configuration'),
			},
			{
				name: 'printEndCustomer',
				label: t('serviceProtocol:protocolPrintout-endCustomer'),
			},
			{
				name: 'printCampaigns',
				label: t('serviceProtocol:protocolPrintout-campaigns'),
			},
			{
				name: 'printCoverage',
				label: t('serviceProtocol:protocolPrintout-coverages'),
			},
			...(!isOffline
				? [
						{
							name: 'printProposals' as const,
							label: t(
								'serviceProtocol:protocolPrintout-proposals'
							),
							helperText: t(
								'serviceProtocol:protocolPrintout-proposals-helper'
							),
						},
						{
							name: 'printServiceActivitiesPhotos' as const,
							label: t(
								'serviceProtocol:protocolPrintout-activitiesPhotos'
							),
						},
					]
				: []),
			{
				name: 'printAdditionalRepairs',
				label: isOffline
					? t(
							'serviceProtocol:protocolPrintout-additionalRepairsOffline'
						)
					: t('serviceProtocol:protocolPrintout-additionalRepairs'),
			},
			{
				name: 'printAdditionalPhotos',
				label: t(
					'serviceProtocol:protocolPrintout-additionalRepairs-photos'
				),
			},
			{
				name: 'printSignatures',
				label: t('serviceProtocol:protocolPrintout-signatures'),
			},
		],
		[isDealer, isOffline, t]
	);

	return (
		<>
			<PdfPrintFormLayout
				control={control}
				onSubmit={onSubmitWithSaveSettings}
				onChange={onChangeHandler}
				isPreview={isPreview}
				isPending={isPending}
				description={t('serviceProtocol:protocolPrintout-info-content')}
				dealerTabLabel={t(
					'serviceProtocol:protocolPrintout-forDealder'
				)}
				customerTabLabel={t(
					'serviceProtocol:protocolPrintout-forCustomer'
				)}
				isDealer={isDealer}
				onTabChange={onTabChange}
				additionalInfoTitle={t(
					'serviceProtocol:protocolPrintout-additionalInformation'
				)}
				commentBoxLabel={t(
					'serviceProtocol:protocolPrintout-additionalInformation-alt'
				)}
				switchSectionLabel={t(
					'serviceProtocol:protocolPrintout-switchLabel'
				)}
				switches={switches}
				preview={{
					isLoadingPreview: isLoadingPreview,
					previewUrl: previewUrl,
					objectName: 'ServiceProtocolPdf',
					generateButtonLabel: t(
						'serviceProtocol:protocolPrintout-generate-preview-button'
					),
					onGenerateClick: onButtonClick,
				}}
				onClose={onClose}
			/>
			<PrintableAttachments
				attachments={protocolData?.attachmentsIds ?? []}
			/>
		</>
	);
};
